import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  CheckCircle2,
  Database,
  BarChart3,
  Globe,
  BookOpen,
  Package,
  Phone,
  Truck,
  Ship,
  Mail,
  Pencil,
  PlugZap,
  Power,
  Trash2,
  Wrench,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { integrationsApi } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import type { IntegrationStatus, IntegrationType } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatDate } from '@/lib/utils';

const TYPE_ICONS: Record<IntegrationType, typeof Database> = {
  elastic: Database,
  bigquery: BarChart3,
  api: Globe,
  rag: BookOpen,
  'upela-api': Package,
  zoho: Phone,
  ugo: Truck,
  'shipper-portal': Ship,
  email: Mail,
};

interface Props {
  instance: IntegrationStatus;
  onEdit: () => void;
  onMutated: () => void;
}

export function IntegrationInstanceCard({ instance, onEdit, onMutated }: Props): JSX.Element {
  const { t } = useTranslation();
  const Icon = TYPE_ICONS[instance.type] ?? Database;

  const testMutation = useMutation({
    mutationFn: () => integrationsApi.test(instance.instanceId),
    onSuccess: (res) => {
      if (res.ok) toast.success(t('integrations.testOkToast', { title: instance.label }));
      else toast.error(t('integrations.testKoToast', { title: instance.label, error: res.error ?? 'fail' }));
      onMutated();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => integrationsApi.setEnabled(instance.instanceId, enabled),
    onSuccess: () => {
      onMutated();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.delete(instance.instanceId),
    onSuccess: (res) => {
      toast.success(t('integrations.deletedToast', { title: instance.label }));
      if (res.orphanedTools.length > 0) {
        toast.warning(
          t('integrations.orphanedToolsWarning', { count: res.orphanedTools.length }),
        );
      }
      onMutated();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const handleDelete = (): void => {
    if (!window.confirm(t('integrations.confirmDelete', { title: instance.label }))) return;
    deleteMutation.mutate();
  };

  return (
    <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold">{instance.label}</CardTitle>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {instance.type}
                </Badge>
                {instance.toolsCount != null && instance.toolsCount > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                    <Wrench className="h-2.5 w-2.5" />
                    {instance.toolsCount}
                  </span>
                )}
              </div>
            </div>
          </div>
          <StatusBadge instance={instance} />
        </div>
        {instance.description && (
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">{instance.description}</p>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-1 text-[11px] uppercase text-muted-foreground">
          <Pair label={t('integrations.updatedAt')} value={instance.updatedAt ? formatDate(instance.updatedAt) : '—'} />
          <Pair label={t('integrations.by')} value={instance.updatedBy ?? '—'} />
          <Pair
            label={t('integrations.lastTest')}
            value={instance.lastTestAt ? formatDate(instance.lastTestAt) : '—'}
          />
          <Pair
            label={t('integrations.lastResult')}
            value={
              instance.lastTestStatus === 'ok'
                ? t('integrations.lastResultOk')
                : instance.lastTestStatus === 'fail'
                  ? t('integrations.lastResultFail')
                  : '—'
            }
          />
        </div>
        {instance.lastTestStatus === 'fail' && instance.lastTestError ? (
          <p className="truncate text-[10px] text-destructive">
            {instance.lastTestError}
          </p>
        ) : null}

        <div className="flex items-center justify-between rounded border bg-muted/20 px-3 py-2 text-xs">
          <span className="flex items-center gap-1.5">
            <Power className="h-3 w-3" />
            {t('integrations.killSwitch')}
          </span>
          <Switch
            checked={instance.enabled}
            onCheckedChange={(v) => toggleMutation.mutate(v)}
            disabled={toggleMutation.isPending}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="default" className="gap-1.5" onClick={onEdit}>
            <Pencil className="h-3 w-3" />
            {t('integrations.modify')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
          >
            <PlugZap className="h-3 w-3" />
            {t('integrations.test')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-3 w-3" />
            {t('integrations.delete')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ instance }: { instance: IntegrationStatus }): JSX.Element {
  const { t } = useTranslation();
  if (!instance.enabled) return <Badge variant="warning">{t('integrations.disabled')}</Badge>;
  if (instance.lastTestStatus === 'fail') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        {t('integrations.testKo')}
      </Badge>
    );
  }
  if (instance.lastTestStatus === 'ok') {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        {t('integrations.ok')}
      </Badge>
    );
  }
  return <Badge variant="outline">{t('integrations.notTested')}</Badge>;
}

function Pair({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span>{label}</span>
      <span className="truncate text-xs normal-case text-foreground">{value}</span>
    </div>
  );
}
